// Shared polished filter/nav dropdown — used by the homepage (select mode) and the
// category/tag landing pages (link mode). Single source of truth for the menu design.
import type { ReactNode } from 'react'

import { useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { Check, ChevronDown, Search } from 'lucide-react'

import { cn } from '@/lib/utils'

import {
    DROPDOWN_CHECK_CLASS,
    DROPDOWN_MENU_CLASS,
    dropdownChevronClass,
    dropdownRowClass,
    dropdownTriggerClass
} from '@/lib/dropdown-styles'

export interface DropdownOption {
    slug: string
    name: string
}

interface FilterDropdownProps {
    label: string
    options: DropdownOption[]
    searchPlaceholder: string
    emptyLabel: string
    /** Slug to highlight as the current selection. */
    activeSlug?: string | null
    /** Selection mode: called when an option (or the reset row) is chosen. */
    onSelect?: (slug: string | null) => void
    /** Link mode: build an href for each option (renders crawlable <Link>s). */
    hrefFor?: (slug: string) => string
    /** Label for the reset / "all" row. Shown in select mode, or in link mode when allHref is set. */
    allLabel?: string
    /** Link mode: href for the "all" reset row. */
    allHref?: string
}

const rowClass = dropdownRowClass

function rowContent(label: string, active: boolean): ReactNode {
    return (
        <>
            <span className='truncate'>{label}</span>
            {active && <Check className={DROPDOWN_CHECK_CLASS} />}
        </>
    )
}

export function FilterDropdown({
    label,
    options,
    searchPlaceholder,
    emptyLabel,
    activeSlug = null,
    onSelect,
    hrefFor,
    allLabel,
    allHref
}: FilterDropdownProps) {
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')

    const rootRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    const current = options.find((option) => option.slug === activeSlug)

    // Focus the search field on open and wire up outside-click / Escape to close.
    useEffect(() => {
        if (!open) return

        setSearch('')
        const focusId = window.setTimeout(() => inputRef.current?.focus(), 10)

        function onPointer(event: MouseEvent) {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false)
        }

        function onKey(event: KeyboardEvent) {
            if (event.key === 'Escape') setOpen(false)
        }

        document.addEventListener('mousedown', onPointer)
        document.addEventListener('keydown', onKey)

        return () => {
            window.clearTimeout(focusId)
            document.removeEventListener('mousedown', onPointer)
            document.removeEventListener('keydown', onKey)
        }
    }, [open])

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase()
        if (!q) return options

        return options.filter((option) => option.name.toLowerCase().includes(q))
    }, [options, search])

    const showAllRow = Boolean(onSelect ? allLabel : allHref && allLabel)

    return (
        // Inline zIndex: Webflow ships a `.relative { z-index: 1 }` utility that would
        // otherwise override Tailwind's z-* classes; the open menu must sit above its sibling.
        <div ref={rootRef} className='relative' style={{ zIndex: open ? 20 : undefined }}>
            <button
                type='button'
                aria-haspopup='listbox'
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                className={dropdownTriggerClass(open, 'min-w-[210px] justify-between')}
            >
                <span className={cn('truncate', !current && 'text-white/55')}>{current ? current.name : label}</span>
                <ChevronDown className={dropdownChevronClass(open)} />
            </button>

            {open && (
                <div
                    className={cn(
                        'absolute left-0 top-[calc(100%+8px)] w-[clamp(240px,80vw,320px)]',
                        DROPDOWN_MENU_CLASS
                    )}
                >
                    <div className='flex items-center gap-2 border-b border-white/10 px-3'>
                        <Search className='size-4 shrink-0 text-white/40' />
                        <input
                            ref={inputRef}
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder={searchPlaceholder}
                            className='dropdown-search h-11 w-full bg-transparent placeholder:text-white/35 outline-none'
                        />
                    </div>

                    <div className='max-h-64 overflow-y-auto p-1.5'>
                        {showAllRow &&
                            (hrefFor ? (
                                <Link href={allHref as string} onClick={() => setOpen(false)} className={rowClass(!activeSlug)}>
                                    {rowContent(allLabel as string, !activeSlug)}
                                </Link>
                            ) : (
                                <button
                                    type='button'
                                    onClick={() => {
                                        onSelect?.(null)
                                        setOpen(false)
                                    }}
                                    className={rowClass(!activeSlug)}
                                >
                                    {rowContent(allLabel as string, !activeSlug)}
                                </button>
                            ))}

                        {visible.map((option) => {
                            const active = option.slug === activeSlug

                            return hrefFor ? (
                                <Link
                                    key={option.slug}
                                    href={hrefFor(option.slug)}
                                    onClick={() => setOpen(false)}
                                    className={rowClass(active)}
                                >
                                    {rowContent(option.name, active)}
                                </Link>
                            ) : (
                                <button
                                    key={option.slug}
                                    type='button'
                                    onClick={() => {
                                        onSelect?.(option.slug)
                                        setOpen(false)
                                    }}
                                    className={rowClass(active)}
                                >
                                    {rowContent(option.name, active)}
                                </button>
                            )
                        })}

                        {visible.length === 0 && (
                            <div className='px-3 py-6 text-center text-sm text-white/40'>{emptyLabel}</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
